import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoryApi } from '../api/categoryApi'
import { notify } from '@/shared/utils'
import type { vendorType, Category } from '@/shared/types'

export function useCategories(vendorId: string) {
  const queryClient = useQueryClient()
  const queryKey = ['categories', vendorId]
  const inventoryQueryKey = ['inventory', vendorId]

  const categoriesQuery = useQuery({
    queryKey,
    queryFn: () => categoryApi.fetchCategories(vendorId),
    enabled: !!vendorId,
    // Always refetch when component mounts
    refetchOnMount: 'always',
    // Data is stale immediately
    staleTime: 0,
  })

  const createMutation = useMutation({
    mutationFn: ({ name, type }: { name: string; type: vendorType }) =>
      categoryApi.createCategory(vendorId, name, type),
    onSuccess: (newCategories) => {
      queryClient.setQueryData(queryKey, newCategories)
      notify.success('Category added')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Could not add category')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ categoryId, name }: { categoryId: string; name: string }) =>
      categoryApi.updateCategory(vendorId, categoryId, name),
    onSuccess: async () => {
      // Refetch categories and inventory (items contain category names)
      await Promise.all([
        queryClient.refetchQueries({ queryKey }),
        queryClient.refetchQueries({ queryKey: inventoryQueryKey }),
      ])
      notify.success('Category updated')
    },
    onError: (error: { message?: string }) => {
      notify.error(error?.message || 'Could not update category')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (categoryId: string) => categoryApi.deleteCategory(vendorId, categoryId),
    onMutate: async (categoryId) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey })

      // Get current data
      const previousCategories = queryClient.getQueryData<Category[]>(queryKey)

      // Optimistically remove the category from categories list
      if (previousCategories) {
        queryClient.setQueryData<Category[]>(
          queryKey,
          previousCategories.filter((c) => c.id !== categoryId)
        )
      }

      return { previousCategories }
    },
    onError: (error: { message?: string }, _categoryId, context) => {
      // Rollback on error
      if (context?.previousCategories) {
        queryClient.setQueryData(queryKey, context.previousCategories)
      }
      notify.error(error?.message || 'Could not delete category')
    },
    onSuccess: async () => {
      // Refetch both categories AND inventory to update category references in items
      await Promise.all([
        queryClient.refetchQueries({ queryKey }),
        queryClient.refetchQueries({ queryKey: inventoryQueryKey }),
      ])
      notify.success('Category deleted')
    },
  })

  return {
    categories: categoriesQuery.data || [],
    isLoading: categoriesQuery.isLoading,
    refetch: categoriesQuery.refetch,
    createCategory: createMutation.mutate,
    updateCategory: updateMutation.mutate,
    deleteCategory: deleteMutation.mutate,
    isProcessing: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  }
}
